import Phaser from "phaser";
import enemiesData from "../data/enemies.json";
import mapConfig from "../data/mapConfig.json";
import { Unit } from "../entities/Unit";
import type { LaneNumber } from "../../../../shared/types/CommanderDecision";
import type { GameCommand, SpawnCommand } from "../../../../shared/types/GameCommand";
import { DifficultySystem } from "./DifficultySystem";

type EnemyData = (typeof enemiesData)[number];
type PendingSpawnCommand = SpawnCommand & { budgetReserved?: boolean };

export type AiBudgetReport = {
  lane: LaneNumber;
  purpose: SpawnCommand["purpose"];
  requestedUnits: string[];
  approvedUnits: string[];
  delayedUnits: string[];
  deniedUnits: string[];
  spent: number;
  budgetAfter: number;
  debtUsed: boolean;
  delayMsAdded: number;
};

export class WaveSystem {
  private nextWaveAt = 4200;
  private waveIndex = 0;
  private readonly enemyById = new Map(enemiesData.map((enemy) => [enemy.id, enemy]));
  private readonly commandBudgetCap = 62;
  private readonly commandBudgetReserveFloor = -34;
  private commandBudget = 28;
  private nextDebtAllowedAt = 0;
  pendingCommands: PendingSpawnCommand[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly difficulty: DifficultySystem,
    private readonly animationsEnabled = true
  ) {}

  update(time: number, enemies: Unit[]) {
    if (time >= this.nextWaveAt) {
      this.spawnDefaultWave(enemies);
      this.nextWaveAt = time + Math.max(7200, 10800 - this.waveIndex * 145);
      this.waveIndex += 1;
    }

    for (let index = this.pendingCommands.length - 1; index >= 0; index -= 1) {
      const command = this.pendingCommands[index];
      if (command && (command.delayMs ?? 0) <= 0) {
        this.spawnCommand(command, enemies);
        this.pendingCommands.splice(index, 1);
      }
    }
  }

  tickPending(deltaMs: number) {
    this.commandBudget = Math.min(this.commandBudgetCap, this.commandBudget + (deltaMs / 1000) * this.commandBudgetRate);
    for (const command of this.pendingCommands) {
      command.delayMs = Math.max(0, (command.delayMs ?? 0) - deltaMs);
    }
  }

  delayPressure(ms: number) {
    this.nextWaveAt += ms;
    for (const command of this.pendingCommands) {
      command.delayMs = (command.delayMs ?? 0) + ms;
    }
  }

  applyGameCommand(command: GameCommand, enemies: Unit[]) {
    const reports: AiBudgetReport[] = [];
    this.difficulty.applyDelta(command.difficultyAdjustment);
    for (const spawn of command.spawnCommands) {
      const adjustedSpawn = {
        ...spawn,
        units: command.preferredEnemyRoles?.length ? this.applyRolePreference(spawn.units, command.preferredEnemyRoles) : spawn.units
      };
      const budgetedSpawn = this.spendBudgetForSpawn(adjustedSpawn);
      reports.push(budgetedSpawn.report);
      if (budgetedSpawn.immediate && (budgetedSpawn.immediate.delayMs ?? 0) > 0) {
        this.pendingCommands.push({ ...budgetedSpawn.immediate, budgetReserved: true });
      } else if (budgetedSpawn.immediate) {
        this.spawnCommand(budgetedSpawn.immediate, enemies);
      }
      if (budgetedSpawn.delayed) {
        this.pendingCommands.push({ ...budgetedSpawn.delayed, budgetReserved: true });
      }
    }
    return reports;
  }

  get budgetSummary() {
    return {
      current: Number(this.commandBudget.toFixed(1)),
      cap: this.commandBudgetCap,
      recoveryPerSecond: Number(this.commandBudgetRate.toFixed(2)),
      pendingCommands: this.pendingCommands.length
    };
  }

  enemyBudgetCost(enemyId: string) {
    const data = this.enemyById.get(enemyId);
    if (!data) return 4;
    if (data.id === "siege_cart" || data.id === "heavy_ram" || data.id === "armored_beast") return data.id === "siege_cart" ? 16 : 18;
    if (data.id === "warlock" || data.id === "zealot" || data.id === "necromancer" || data.id === "plague_bishop") return 12;
    if (data.id === "assassin" || data.id === "black_assassin" || data.id === "knight") return 10;
    if (data.id === "shield_soldier") return 8;
    if (data.id === "spearman" || data.id === "bomber") return 6;
    if (data.id === "scout_wolf") return 5;
    return 4;
  }

  private scheduleDelayForCost(cost: number, existingDelay = 0) {
    return Math.min(15000, Math.max(existingDelay, Math.ceil((cost / this.commandBudgetRate) * 1000) + 1000));
  }

  private spendBudgetForSpawn(command: SpawnCommand): { immediate?: SpawnCommand; delayed?: SpawnCommand; report: AiBudgetReport } {
    const requestedUnits = command.units.slice(0, 5);
    const approvedUnits: string[] = [];
    const delayedUnits: string[] = [];
    const deniedUnits: string[] = [];
    let spent = 0;
    let delayedCost = 0;
    let debtUsed = false;

    for (const enemyId of requestedUnits) {
      const cost = this.enemyBudgetCost(enemyId);
      if (this.commandBudget >= cost) {
        this.commandBudget -= cost;
        spent += cost;
        approvedUnits.push(enemyId);
        continue;
      }
      if (this.canUseBudgetDebt(command, cost)) {
        this.commandBudget -= cost;
        spent += cost;
        debtUsed = true;
        this.nextDebtAllowedAt = this.scene.time.now + 30000;
        approvedUnits.push(enemyId);
        continue;
      }
      if (this.commandBudget - cost >= this.commandBudgetReserveFloor) {
        this.commandBudget -= cost;
        spent += cost;
        delayedCost += cost;
        delayedUnits.push(enemyId);
      } else {
        deniedUnits.push(enemyId);
      }
    }

    const immediate = approvedUnits.length > 0 ? { ...command, units: approvedUnits } : undefined;
    const delayMsAdded = delayedUnits.length > 0 ? this.scheduleDelayForCost(delayedCost, command.delayMs ?? 0) : 0;
    const delayed = delayedUnits.length > 0 ? { ...command, units: delayedUnits, delayMs: delayMsAdded } : undefined;
    return {
      immediate,
      delayed,
      report: {
        lane: command.lane,
        purpose: command.purpose,
        requestedUnits,
        approvedUnits,
        delayedUnits,
        deniedUnits,
        spent,
        budgetAfter: Number(this.commandBudget.toFixed(1)),
        debtUsed,
        delayMsAdded
      }
    };
  }

  private spawnDefaultWave(enemies: Unit[]) {
    const lane = (((this.waveIndex % 5) + 1) as LaneNumber);
    const pattern =
      this.waveIndex > 16 && this.waveIndex % 6 === 0
        ? ["heavy_ram", "knight", "plague_bishop"]
        : this.waveIndex > 11 && this.waveIndex % 5 === 0
          ? ["necromancer", "bomber", "raider"]
          : this.waveIndex > 7 && this.waveIndex % 4 === 0
            ? ["black_assassin", "knight"]
            : this.waveIndex % 4 === 0
              ? ["shield_soldier", "raider"]
              : this.waveIndex % 5 === 0
                ? ["spearman", "raider", "scout_wolf"]
                : ["raider", "raider"];
    this.spawnCommand({ lane, units: pattern, purpose: "main_attack" }, enemies);
  }

  private applyRolePreference(units: string[], roles: string[]) {
    const candidates = [...this.enemyById.values()].filter((enemy) => roles.includes(enemy.role));
    if (candidates.length === 0) return units;
    return units.map((unit, index) => (index === units.length - 1 ? Phaser.Utils.Array.GetRandom(candidates).id : unit));
  }

  private spawnCommand(command: SpawnCommand, enemies: Unit[]) {
    this.showSpawnWarning(command.lane);
    command.units.slice(0, 5).forEach((enemyId, index) => {
      const data = this.enemyById.get(enemyId) ?? this.enemyById.get("raider");
      if (!data) return;
      this.scene.time.delayedCall(index * 420, () => this.spawnEnemy(data, command.lane, enemies));
    });
  }

  private get commandBudgetRate() {
    return 0.85 + this.difficulty.pressure * 0.24;
  }

  private canUseBudgetDebt(command: SpawnCommand, cost: number) {
    if (this.scene.time.now < this.nextDebtAllowedAt) return false;
    if (cost > 12) return false;
    return command.purpose === "counter" || command.purpose === "main_attack";
  }

  private showSpawnWarning(lane: LaneNumber) {
    const laneConfig = mapConfig.lanes.find((item) => item.lane === lane) ?? mapConfig.lanes[2]!;
    const warning = this.scene.add.image(mapConfig.enemySpawnX + 34, laneConfig.y, "spawn_warning").setDepth(68).setScale(0.58).setAlpha(0.92);
    this.scene.tweens.add({
      targets: warning,
      alpha: 0,
      scale: 0.78,
      duration: 900,
      onComplete: () => warning.destroy()
    });
  }

  spawnEnemyById(enemyId: string, lane: LaneNumber, enemies: Unit[], x?: number) {
    const data = this.enemyById.get(enemyId) ?? this.enemyById.get("raider");
    if (data) this.spawnEnemy(data, lane, enemies, x);
  }

  private spawnEnemy(data: EnemyData, lane: LaneNumber, enemies: Unit[], x = mapConfig.enemySpawnX) {
    const laneConfig = mapConfig.lanes.find((item) => item.lane === lane) ?? mapConfig.lanes[2]!;
      const unit = new Unit(this.scene, {
      id: data.id,
      name: data.name,
      lane,
      x,
      y: laneConfig.y,
      faction: "enemy",
      role: data.role,
      texture: this.animationsEnabled ? `enemy_${data.id}_walk` : `enemy_${data.id}`,
      hp: Math.round(data.hp * this.difficulty.enemyHpMultiplier),
      damage: data.damage,
      range: data.role === "summoner" || data.role === "dot" ? 128 : 44,
      attackRateMs: 1050,
      speed: data.speed * this.difficulty.enemySpeedMultiplier,
      castleDamage: data.castleDamage,
      goldReward: data.goldReward,
      ability: "ability" in data ? String(data.ability) : "none",
      targetPriority: "targetPriority" in data ? data.targetPriority as "units" | "towers" | "castle" | "siege" : "units",
      siegeDamageMultiplier: "siegeDamageMultiplier" in data ? Number(data.siegeDamageMultiplier) : 1,
      deployTimeMs: "deployTimeMs" in data ? Number(data.deployTimeMs) : 0,
      convertChance: "convertChance" in data ? Number(data.convertChance) : 0,
      dot: "dot" in data ? data.dot : undefined,
      summon: "summon" in data ? String(data.summon) : undefined,
      explodeOnDeath: "explodeOnDeath" in data ? data.explodeOnDeath : undefined
    });
    enemies.push(unit);
  }

}
